/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { InternalRating, WatchFromSoCuAction } from 'client-code/action-details';
import {
    ActionKernelType,
    InfoCardKernel,
    SeasonOrYearsDetail,
    WatchFromSocuActionKernel,
} from 'client-code/actions';

import { DeviceModel } from '../../core/device';
import { BrandingUiInfoKeys } from '../../core/entities';
import { ContentType } from '../../core/enums';
import { IInfoCard, InfoCardManager } from '../../core/infocard';
import { NdvrInteractor, VodInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log, VuLogger } from '../../core/logging';
import { KeyManager } from '../../core/navigation';
import { ScreenController } from '../../core/navigation/ScreenController';
import { NetworkManager } from '../../core/network/NetworkManager';
import { PlaybackContentType } from '../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../core/playback/impls';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import type { AppDispatch, TGetState, TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import { TVodPlayActionStateType } from '../../core/types';
import { TProgramScreenState } from '../../core/types/TProgramScreenState';
import { UIOrchestrator, UIOrchestratorState } from '../../core/uiorchestrator';
import { ProgramScreenInfoCardUserInputHandler } from '../../core/usecases/ProgramScreenInfoCardUserInputHandler';
import { BrandingUiUtility, UniqueIdentifier } from '../../core/utils';
import { CcbSwitch } from '../../core/utils/ccb';
import { isJumpChannelCcbSwitchEnabled } from '../../core/utils/ccb/CcbSwitchHelper';
import { CcbSwitchName } from '../../core/utils/ccb/enums';
import { NdvrPlaybackType } from '../../core/videoplayer/enums';
import { IPlayable } from '../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import { BiaxialActionWidgetHelper } from '../../views/ProgramScreen/BiaxialActionWidget/BiaxialActionWidgetHelper';
import { ProgramScreenHelper } from '../../views/ProgramScreen/ProgramScreenHelper';
import { WhisperTypes } from '../../views/Whisper/WhisperTypes';
import {
    setListEntryActions,
    setListEntryPageActions,
} from '../listscreenview/listviewscreen.actions';
import { prepareCreateOnePassOverlay } from '../overlay/createonepassoverlay/createonepassoverlay.reducres';
import { showDeleteOverlay } from '../overlay/deleteoverlay/deleteoverlay.reducers';
import { prepareKeepUntilRecordingOverlay } from '../overlay/keepuntilrecordingoverlay/keepuntillrecordingoverlay.reducers';
import { prepareModifyOnePassOverlay } from '../overlay/modifyonepassoverlay/modifyonepassoverlay.reducers';
import { prepareCancelOnePassOverlay } from '../overlay/onepassoptionsoverlay/onepassoptionsoverlay.reducers';
import { showPCPinOverlayWithProgramRating } from '../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    showCancelRecordingOverlay,
    showRecordingOverlay,
    showStopAndDeleteRecordingOverlay,
} from '../overlay/recordingoverlay/recordingoverlay.reducers';
import {
    prepareStreamingOptionsOverlay,
    StreamingOptionsOverlayType,
} from '../overlay/streamingoptionsoverlay/streamingoptionsoverlay.reducers';
import { prepareVodResumeStartOverOverlayForWatchAction } from '../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import { programScreenTransition } from '../screens/navigation/screensnavigation';
import {
    showGuideScreen,
    showListViewScreen,
    showPreviousScreen,
} from '../screens/screens.reducers';
import { startVideoPlayback } from '../videoplayer/videoplayer.reducers';
import { notifyVodPlayActionState } from '../vodplayaction/vodplayaction.reducers';
import { showWhisper } from '../whisper/whisper.reducer';
import {
    initAndResetBiaxialVerticalStripProgramScreenAction,
    initBiaxialHorizontalStripProgramScreenAction,
    resetBiaxialHorizontalStripProgramScreenAction,
    setCurrentHighlightIndexProgramScreenAction,
    setCurrentVerticalHighlightIndexProgramScreenAction,
    setHorizontalOptionsProgramScreenAction,
    setLeftGradientVisibleAction,
    setRightGradientVisibleAction,
    setTotalItemsProgramScreenActions,
    setViewAllProgramScreenAction,
} from './programscreen.actions';
import {
    INIT_AND_RESET_BIAXIAL_VERTICAL_STRIP_PROGRAM_SCREEN,
    INIT_BIAXIAL_HORIZONTAL_STRIP_PROGRAM_SCREEN,
    RESET_BIAXIAL_HORIZONTAL_STRIP_PROGRAM_SCREEN,
    SET_CURRENT_HIGHLIGHT_INDEX_PROGRAM_SCREEN,
    SET_CURRENT_TOTAL_ITEMS_PROGRAM_SCREEN,
    SET_CURRENT_VERTICAL_HIGHLIGHT_INDEX_PROGRAM_SCREEN,
    SET_GRADIENT_FOR_PROGRAM_SCREEN,
    SET_HORIZONTAL_OPTIONS_PROGRAM_SCREEN,
    SET_LEFT_GRADIENT_VISIBILITY,
    SET_PROGRAM_SCREEN_CARET,
    SET_RIGHT_GRADIENT_VISIBILITY,
    SET_VIEW_ALL_DIRECTION_PROGRAM_SCREEN,
} from './programscreen.types';
import { ProgramScreenConstants } from './ProgramScreenConstants';

const TAG = 'programscreen';

type ProgramScreenState = {
    currentHighlightIndex: number;
    currentVerticalHighlightIndex: number;
    horizontalOptions: any;
    totalItems: number;
    totalVerticalItems: number;
    isCaretVisible: boolean;
    isGradientVisible: boolean;
    showRightGradient: boolean;
    showLeftGradient: boolean;
    viewAllDirection: 'left' | 'right' | 'none';
};

const initialState: ProgramScreenState = {
    currentHighlightIndex: 0,
    currentVerticalHighlightIndex: 0,
    totalItems: 0,
    horizontalOptions: [],
    totalVerticalItems: 0,
    isCaretVisible: false,
    isGradientVisible: false,
    showRightGradient: false,
    showLeftGradient: false,
    viewAllDirection: 'none',
};

const programScreenReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<ProgramScreenState>>{},
) => {
    switch (type) {
        case INIT_BIAXIAL_HORIZONTAL_STRIP_PROGRAM_SCREEN:
            return {
                ...state,
                currentHighlightIndex: payload.currentHighlightIndex,
                totalItems: payload.totalItems,
            };
        case INIT_AND_RESET_BIAXIAL_VERTICAL_STRIP_PROGRAM_SCREEN:
            return {
                ...state,
                currentVerticalHighlightIndex: payload.currentVerticalHighlightIndex,
                totalVerticalItems: payload.totalVerticalItems,
            };
        case RESET_BIAXIAL_HORIZONTAL_STRIP_PROGRAM_SCREEN:
            return {
                ...state,
                currentHighlightIndex: payload.currentHighlightIndex,
                totalItems: payload.totalItems,
            };
        case SET_CURRENT_HIGHLIGHT_INDEX_PROGRAM_SCREEN:
            return {
                ...state,
                currentHighlightIndex: payload.currentHighlightIndex,
            };
        case SET_CURRENT_VERTICAL_HIGHLIGHT_INDEX_PROGRAM_SCREEN:
            return {
                ...state,
                currentVerticalHighlightIndex: payload.currentVerticalHighlightIndex,
            };

        case SET_PROGRAM_SCREEN_CARET:
            return {
                ...state,
                isCaretVisible: payload.isCaretVisible,
            };
        case SET_CURRENT_TOTAL_ITEMS_PROGRAM_SCREEN:
            return {
                ...state,
                totalItems: payload.totalItems,
            };
        case SET_HORIZONTAL_OPTIONS_PROGRAM_SCREEN: {
            return {
                ...state,
                horizontalOptions: payload.horizontalOptions,
            };
        }
        case SET_GRADIENT_FOR_PROGRAM_SCREEN:
            return {
                ...state,
                isGradientVisible: payload.isGradientVisible,
            };
        case SET_RIGHT_GRADIENT_VISIBILITY:
            return {
                ...state,
                showRightGradient: payload.showRightGradient,
            };
        case SET_LEFT_GRADIENT_VISIBILITY:
            return {
                ...state,
                showLeftGradient: payload.showLeftGradient,
            };
        case SET_VIEW_ALL_DIRECTION_PROGRAM_SCREEN:
            return {
                ...state,
                viewAllDirection: payload.viewAllDirection,
            };
        default:
            return state;
    }
};

const saveProgramScreenSticknessInfo = () => (dispatch: AppDispatch, getState: TGetState) => {
    const { currentHighlightIndex, currentVerticalHighlightIndex } = getState().programScreen;
    const currScreen = ScreenController.getInstance().getScreen();
    const currScreenProperties = ScreenController.getInstance().getScreen().getProperties()!;
    currScreen.setProperties({
        ...currScreenProperties,
        horizontalIndex: currentHighlightIndex,
        verticalIndex: currentVerticalHighlightIndex,
        currentImageIndex: BiaxialActionWidgetHelper.getCurrentImageIndex(),
    });
};

/**
 * Performs a parental control check before dispatching an action based on the adult status.
 *
 * @param isAdult - Indicates whether the content is marked as adult.
 * @param callBack - The callback function to be executed if the parental control check passes.
 * @param callBackParams - Additional parameters to be passed to the callback function.
 * @returns A function that takes dispatch as an argument and performs the parental control check.
 */

const doAdultParentalControlCheck =
    (isAdult: boolean, callBack: Function, ...callBackParams: any[]) =>
    (dispatch: Function) => {
        const services = new NdvrInteractor();
        if (isAdult && services.shouldHideMetadata()) {
            dispatch(
                showPCPinOverlayWithProgramRating(() => {
                    dispatch(callBack(...callBackParams));
                }, isAdult),
            );
            return;
        }
        dispatch(callBack(...callBackParams, showWhisper));
    };

/**
 * Performs a parental control check before dispatching an action based on internal rating
 * and adult.
 *
 * @param action - The action to be dispatched after the parental control check.
 * @param callback - The callback function to be executed if the parental control check passes.
 * @param optionalParams - Additional optional parameters to be passed to the callback function.
 * @returns A function that takes dispatch as an argument and performs the parental control check.
 */
const doRatingsParentalControlCheck =
    (
        contentRatingInfo: {
            internalRatingList: InternalRating[];
            isAdult: boolean;
            contentType: string;
        },
        callback: Function,
        ...optionalParams: any[]
    ) =>
    (dispatch: Function) => {
        const { internalRatingList, isAdult, contentType } = contentRatingInfo;

        const services = new NdvrInteractor();
        const doesPcViolated = services.checkPCViolationsWithRatings(
            internalRatingList,
            isAdult,
            contentType,
        );
        const ratingName = services.getRestrictedRatingName(internalRatingList);
        if (doesPcViolated) {
            dispatch(
                showPCPinOverlayWithProgramRating(
                    () => {
                        dispatch(callback(...optionalParams));
                    },
                    isAdult,
                    ratingName,
                ),
            );
        } else {
            dispatch(callback(...optionalParams, showWhisper));
        }
    };

const playLiveTvFromProgramScreen = (action: any) => async (dispatch: Function) => {
    try {
        const { channelNumber } = action.kernel;
        const services = new VodInteractor();
        const getChannelFromChannelNumber = isJumpChannelCcbSwitchEnabled()
            ? services.getChannelFromChannelNumberV2
            : services.getChannelFromChannelNumber;
        const channel = await getChannelFromChannelNumber(channelNumber);
        const playbackModelImpl = PlaybackDataModelImpl.getInstance();
        const livePlayable: IPlayable = {
            channel,
            playbackContentType: PlaybackContentType.LIVE,
        };
        if (playbackModelImpl.isSocu()) {
            playbackModelImpl.resetPlaybackData();
        } else {
            playbackModelImpl.resetNewPlaybackData();
        }
        if (
            channel.channelNumber !==
            VideoPlaybackController.getInstance().getPlayable()?.channel?.channelNumber
        ) {
            VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
        }
        dispatch(startVideoPlayback(livePlayable));
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
        Log.e(TAG, 'Failed to play live TV from program screen.', errorMessage);
        Diagnostics.error(TAG, 'Failed to play live TV from program screen.', errorMessage);
        dispatch(
            showWhisper({
                message: Locale.getString(LocaleKeys.PLAYBACK_NOT_SUPPORTED_WHISPER_MESSAGE),
                iconType: WhisperTypes.NO_ICON,
            }),
        );
    }
};

const playRecordingFromProgramScreen =
    (action: any, isStartOver: boolean) => async (dispatch: Function) => {
        const { isRecordingInProgress, recordingId, internalRatingList, isAdult, contentType } =
            action.kernel;
        const playbackModelImpl = PlaybackDataModelImpl.getInstance();
        playbackModelImpl.setPlaybackData(
            isRecordingInProgress
                ? PlaybackContentType.NDVR_LIVE
                : PlaybackContentType.NDVR_RECORDED,
        );
        const playable: IPlayable = {
            playbackContentType: isRecordingInProgress
                ? PlaybackContentType.NDVR_LIVE
                : PlaybackContentType.NDVR_RECORDED,
            ndvrPlaybackModel: {
                id: recordingId,
                ndvrType: NdvrPlaybackType.RECORDING,
                isStartOver,
            },
        };

        // Start playback if parental control check passes.
        dispatch(
            doRatingsParentalControlCheck(
                { internalRatingList, isAdult, contentType },
                startVideoPlayback,
                playable,
            ),
        );
    };

const createBookmark =
    (type: 'content' | 'collection', action: any) => async (dispatch: Function) => {
        try {
            const services = new NdvrInteractor();
            if (type === 'content') {
                await services
                    .getRequestModelToCreateBookmarkContentIdSchedule(
                        action.kernel.contentId,
                        action.kernel.collectionId,
                    )
                    .send();
            } else {
                await services
                    .getRequestModelToCreateBookmarkCollectionIdSchedule(
                        action.kernel.collectionId,
                        {
                            year: action.kernel.year,
                            season: action.kernel.seasonNumber,
                        },
                    )
                    .send();
            }
            const bookmarkActionId = action.kernel.actionId.split('_');
            const bookmarkType =
                bookmarkActionId[type === 'content' ? bookmarkActionId.length - 1 : 1];
            let whisperMessage = Locale.getString(
                type === 'content'
                    ? LocaleKeys[`BOOKMARK_WHISPER_MESSAGE_FOR_${bookmarkType}_TYPE`]
                    : LocaleKeys[`BOOKMARK_ALL_WHISPER_MESSAGE_FOR_${bookmarkType}_TYPE`],
            );
            if (type === 'collection') {
                whisperMessage = action.kernel.year
                    ? whisperMessage.replace('YEAR_NUMBER', action.kernel.year)
                    : whisperMessage.replace('SEASON_NUMBER', action.kernel.seasonNumber);
            }
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.BOOKMARK_CREATED);
            dispatch(
                showWhisper({
                    message: whisperMessage,
                    iconType: WhisperTypes.NO_ICON,
                }),
            );
        } catch (_error) {
            const whisperMessage = Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE);
            dispatch(
                showWhisper({
                    message: whisperMessage,
                    iconType: WhisperTypes.NO_ICON,
                }),
            );
            const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
            Log.e('ProgramScreen', errorMessage);
            Diagnostics.error('ProgramScreen', errorMessage);
        }
    };

const setRecordingSharedPref = (recordParams: {
    contentId: string;
    offerId: string;
    cancelActionId?: string | undefined;
}) => {
    const recordingSharedPreferenceEditor = DeviceModel.getSharedPreferences(
        SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
    ).edit();

    recordingSharedPreferenceEditor.putString(
        SharedPrefConstants.RECORDING_OFFER_ID,
        recordParams.offerId,
    );

    recordingSharedPreferenceEditor.putString(
        SharedPrefConstants.RECORDING_CONTENT_ID,
        recordParams.contentId,
    );

    if (recordParams.cancelActionId) {
        recordingSharedPreferenceEditor.putString(
            SharedPrefConstants.RECORDING_CANCEL_ACTION_ID,
            recordParams.cancelActionId,
        );
    }
    recordingSharedPreferenceEditor.commit();
};

const launchSoCuPlaybackFromProgramScreen = (action: any) => async (dispatch: Function) => {
    const {
        isCatchUp,
        isStartOver,
        socuOfferId: offerId,
    } = action.kernel as WatchFromSocuActionKernel;
    const playbackModelImpl = PlaybackDataModelImpl.getInstance();
    const actionDetailsList = await playbackModelImpl.fetchActionDetails({
        isCatchUp: !!isCatchUp,
        isStartOver: !!isStartOver,
        screenType: 'contentScreen',
        offerId,
    });
    playbackModelImpl.setActionDetailsList(actionDetailsList);
    const watchAction: WatchFromSoCuAction = actionDetailsList.actionDetails.watchActionsList[0];
    const { contentType = '' } = actionDetailsList.actionDetails;
    const { internalRatingList = [], isAdult = false } = watchAction.kernel as any;

    // Start SoCu playback if parental control check passes.
    dispatch(
        doRatingsParentalControlCheck(
            { internalRatingList, isAdult, contentType },
            prepareVodResumeStartOverOverlayForWatchAction,
            watchAction,
            isStartOver,
        ),
    );
};

export const initBiaxialHorizontalStrip =
    (currentHighlightIndex: number, totalItems: number) => (dispatch: Function) => {
        dispatch(initBiaxialHorizontalStripProgramScreenAction(currentHighlightIndex, totalItems));
    };

export const initAndResetBiaxialVerticalStrip =
    (currentVerticalHighlightIndex: number, totalVerticalItems: number) => (dispatch: Function) => {
        dispatch(
            initAndResetBiaxialVerticalStripProgramScreenAction(
                currentVerticalHighlightIndex,
                totalVerticalItems,
            ),
        );
    };

export const resetBiaxialHorizontalStrip = () => (dispatch: Function) => {
    dispatch(resetBiaxialHorizontalStripProgramScreenAction());
};

export const setTotalItemsProgramScreen = (totalCount: number) => (dispatch: Function) => {
    dispatch(setTotalItemsProgramScreenActions(totalCount));
};
export const setHorizontalOptionsProgramScreen =
    (horizontalOptions: any) => (dispatch: Function) => {
        dispatch(setHorizontalOptionsProgramScreenAction(horizontalOptions));
    };
export const onKeyBackProgramScreenHandler = () => (dispatch: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showGuideScreen());
        return;
    }
    dispatch(showPreviousScreen());
};

export const onKeyDownProgramScreenHandler = () => (dispatch: Function, getState: Function) => {
    const { programScreen } = getState();
    const { totalVerticalItems, currentVerticalHighlightIndex } = programScreen;
    const newHighlightIndex = Math.min(currentVerticalHighlightIndex + 1, totalVerticalItems - 1);
    dispatch(setCurrentVerticalHighlightIndexProgramScreenAction(newHighlightIndex));
    if (currentVerticalHighlightIndex !== totalVerticalItems - 1) {
        dispatch(setHorizontalOptionsProgramScreenAction([]));
        BiaxialActionWidgetHelper.reset();
    }
};

export const updateViewAllDirectionProgramScreen =
    () => (dispatch: Function, getState: TGetState) => {
        const { programScreen } = getState();
        const { totalItems, currentHighlightIndex, horizontalOptions } = programScreen;
        const currentItem = horizontalOptions[currentHighlightIndex];
        if (!currentItem) return;
        const layoutType = currentItem.displayDetails.layout;
        const optionDetails = currentItem.type;

        if (
            optionDetails === ProgramScreenConstants.TEXT ||
            currentItem.displayDetails.identifier === ProgramScreenConstants.VIEW_ALL
        ) {
            dispatch(setViewAllProgramScreenAction('none'));
            return;
        }

        const isStaticDataStrip = [
            ProgramScreenConstants.ALL_EPISODES,
            ProgramScreenConstants.ON_DEMAND_EPISODES,
            ProgramScreenConstants.CAST,
        ].includes(layoutType);
        const idx = !isStaticDataStrip
            ? BiaxialActionWidgetHelper.getCurrentImageIndex()
            : currentHighlightIndex;
        const tCount = !isStaticDataStrip ? BiaxialActionWidgetHelper.getTotalCount()! : totalItems;
        // optionDetails === 'LandscapeImage -> contentType = movie
        const limit = optionDetails === 'LandscapeImage' ? 8 : 4;
        const precedingTileCount = optionDetails === 'LandscapeImage' && tCount >= 8 ? 2 : 1;
        const viewAllTileIndex = !isStaticDataStrip ? tCount - 1 : 0;

        let leftCornerOffset = idx - precedingTileCount;
        let rightCornerOffset = idx + (Math.min(limit, tCount) - (precedingTileCount + 1));
        const hasViewAllInCurrentState = !isStaticDataStrip
            ? leftCornerOffset < 0 || rightCornerOffset >= tCount - 1
            : leftCornerOffset <= 0 || rightCornerOffset > tCount - 1;

        if (hasViewAllInCurrentState) {
            dispatch(setViewAllProgramScreenAction('none'));
            return;
        }

        // actual offset i.e wrt to circular list
        leftCornerOffset = (leftCornerOffset + tCount) % tCount;
        rightCornerOffset %= tCount;

        const circularDist = (a: number, b: number) =>
            Math.min(Math.abs(a - b), tCount - Math.abs(a - b));

        if (
            circularDist(viewAllTileIndex, leftCornerOffset) <=
            circularDist(viewAllTileIndex, rightCornerOffset)
        )
            dispatch(setViewAllProgramScreenAction('left'));
        else dispatch(setViewAllProgramScreenAction('right'));
    };

const transitionToProgramScreen =
    (id: string, contentType: ContentType) => async (dispatch: Function) => {
        let isAdult = false;

        try {
            if (!KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().startKeyIgnoring();

            const service = new NdvrInteractor();

            switch (contentType) {
                case ContentType.OFFER_ID:
                    isAdult = !!(await service.getOrFetchOfferPreviewInfo(id)).offerPreview
                        .contentPreview?.isAdult;
                    break;
                case ContentType.CONTENT_ID:
                    isAdult = !!(await service.getContentPreviewV2ForContentId(id)).isAdult;
                    break;
                default:
            }
        } catch (error) {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
            Log.e(TAG, `Failed to get ${contentType} preview info during parental check.`);
            Log.d(TAG, 'stacktrace:', error);
            Diagnostics.error(
                TAG,
                `Failed to get ${contentType} preview info during parental check.`,
            );
        }

        dispatch(
            doAdultParentalControlCheck(isAdult, programScreenTransition, {
                id,
                contentType,
            }),
        );
    };

export const onKeyLeftProgramScreenHandler =
    () => async (dispatch: Function, getState: TGetState) => {
        const { programScreen } = getState();
        const { totalItems, currentHighlightIndex, horizontalOptions } = programScreen;
        if (!horizontalOptions[currentHighlightIndex]) return;
        const layoutType = horizontalOptions[currentHighlightIndex]?.displayDetails?.layout;
        const optionDetails = horizontalOptions[currentHighlightIndex]?.type;
        const isStaticDataStrip = [
            ProgramScreenConstants.ALL_EPISODES,
            ProgramScreenConstants.ON_DEMAND_EPISODES,
            ProgramScreenConstants.CAST,
        ].includes(layoutType);

        if (
            layoutType &&
            !isStaticDataStrip &&
            (optionDetails === 'PosterImage' || optionDetails === 'LandscapeImage')
        ) {
            const tCount = BiaxialActionWidgetHelper.getTotalCount();
            const limit = BiaxialActionWidgetHelper.getLimit();
            if (tCount === null || tCount <= 1) return;

            dispatch(setLeftGradientVisibleAction(true));
            setTimeout(() => {
                dispatch(setLeftGradientVisibleAction(false));
            }, 300);

            const nextIdx = BiaxialActionWidgetHelper.getNextOffset('left');

            // For tCount <= limit, Skip loading state as everything is already cached
            if (tCount > limit) {
                const curIdx = BiaxialActionWidgetHelper.getCurrentImageIndex();
                const loadingState = BiaxialActionWidgetHelper.getLoadingState(
                    curIdx,
                    nextIdx,
                    'left',
                    horizontalOptions,
                );
                dispatch(setHorizontalOptionsProgramScreenAction(loadingState));
            }

            const res = await BiaxialActionWidgetHelper.fetchData(nextIdx);

            // For tCount <= limit, Skip networkId check as everything is cached and static
            if (
                tCount <= limit ||
                res.networkCallId === UniqueIdentifier.getUpcomingNetworkCallId()
            ) {
                dispatch(setHorizontalOptionsProgramScreenAction(res.imageInfo));
                dispatch(updateViewAllDirectionProgramScreen());
            }
            return;
        }

        // TODO: Need to Fix this in KST-11230
        const newHighlightIndex = (currentHighlightIndex - 1 + totalItems) % totalItems;
        if (newHighlightIndex !== currentHighlightIndex)
            dispatch(setCurrentHighlightIndexProgramScreenAction(newHighlightIndex));
        dispatch(updateViewAllDirectionProgramScreen());
    };

const handlePlayableAction = (action: any) => (dispatch: Function) => {
    switch (action.kernel.actionKernelType) {
        case ActionKernelType.StreamingOptionsActionKernel: {
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.FETCHING_DATA);
            PlaybackDataModelImpl.getInstance()
                .fetchOttContentActionDetails(action.kernel.contentId, 'contentScreen')
                .then(() => {
                    UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.DATA_LOADED);
                    dispatch(
                        prepareStreamingOptionsOverlay(
                            StreamingOptionsOverlayType.STREAMING_OPTIONS,
                        ),
                    );
                })
                .catch((error) => {
                    UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.DATA_LOADED);
                    Log.e(
                        TAG,
                        'Failed to fetch ott content action details when invoked for Streaming Options.',
                        error,
                    );
                    Diagnostics.error(
                        TAG,
                        'Failed to fetch ott content action details when invoked for Streaming Options.',
                        error,
                    );
                });
            break;
        }
        case ActionKernelType.WatchFromVodActionKernel: {
            if (
                CcbSwitch.getInstance().isEnabledAndOverriden(
                    CcbSwitchName.ENABLE_VOD_BROWSE_PROGRAM_SCREEN_REMOVE_IN_KST_12208,
                )
            ) {
                dispatch(
                    notifyVodPlayActionState({
                        type: TVodPlayActionStateType.INIT,
                        vodRequiredParams: {
                            itemType: 'content',
                            id: action.kernel.contentId,
                            partnerId: action.kernel.partnerId,
                            collapsedVodItemId: action.kernel.collapsedVodItemId,
                            contentSupplierPartnerId: action.kernel.contentSupplierPartnerId,
                        },
                    }),
                );
                return;
            }
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.FETCHING_DATA);
            PlaybackDataModelImpl.getInstance()
                .fetchOttContentActionDetails(action.kernel.contentId, 'contentScreen')
                .then(() => {
                    UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.DATA_LOADED);
                    dispatch(prepareStreamingOptionsOverlay(StreamingOptionsOverlayType.VOD_SOCU));
                })
                .catch((error) => {
                    UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.DATA_LOADED);
                    Log.e(
                        TAG,
                        'Failed to fetch ott content action details when invoked for VOD_SOCU.',
                        error,
                    );
                    Diagnostics.error(
                        TAG,
                        'Failed to fetch ott content action details when invoked for VOD_SOCU.',
                        error,
                    );
                });
            break;
        }
        case ActionKernelType.WatchFromMyShowsActionKernel:
        case ActionKernelType.ResumeFromMyShowsActionKernel:
            dispatch(
                playRecordingFromProgramScreen(
                    action,
                    action.kernel.actionKernelType ===
                        ActionKernelType.WatchFromMyShowsActionKernel,
                ),
            );
            break;
        case ActionKernelType.WatchLiveActionKernel:
            dispatch(playLiveTvFromProgramScreen(action));
            break;
        case ActionKernelType.WatchFromSocuActionKernel:
            dispatch(launchSoCuPlaybackFromProgramScreen(action));
            break;
        case ActionKernelType.WatchFromVodOfferActionKernel: {
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.INIT,
                    vodRequiredParams: {
                        itemType: 'offer',
                        id: action.kernel.offerId,
                        partnerId: action.provider.providerId,
                    },
                }),
            );
            break;
        }
        case ActionKernelType.WatchVodOfferPreviewActionKernel: {
            dispatch(saveProgramScreenSticknessInfo());
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.INIT,
                    vodRequiredParams: {
                        itemType: 'offer',
                        id: action.kernel.offerId,
                        partnerId: '',
                        isPreview: true,
                    },
                }),
            );
            break;
        }
        case ActionKernelType.WatchVodContentPreviewActionKernel: {
            dispatch(saveProgramScreenSticknessInfo());
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.INIT,
                    vodRequiredParams: {
                        itemType: 'content',
                        id: action.kernel.contentId,
                        partnerId: '',
                        isPreview: true,
                        collapsedVodItemId: '',
                    },
                }),
            );
            break;
        }
        default:
    }
};

export const onKeyPlayProgramScreenHandler = () => (dispatch: Function, getState: TGetState) => {
    const { currentHighlightIndex, horizontalOptions } = getState().programScreen;
    if (!horizontalOptions.length) {
        Log.e(TAG, 'Failed to get horizontal options.');
        Diagnostics.error(TAG, 'Failed to get horizontal options.');
        return;
    }

    const action = horizontalOptions[currentHighlightIndex]?.identifier;
    if (!action) {
        Log.e(
            TAG,
            'Failed to get action from horizontal options.',
            horizontalOptions[currentHighlightIndex],
        );
        Diagnostics.error(
            TAG,
            'Failed to get action from horizontal options.',
            horizontalOptions[currentHighlightIndex],
        );
        return;
    }
    dispatch(handlePlayableAction(action));
};

export const onKeyOkProgramScreenHandler = () => (dispatch: Function, getState: Function) => {
    const service = new NdvrInteractor();

    if (NetworkManager.isNetworkDisconnected()) {
        const whisperMessage = BrandingUiUtility.formatDescription(
            Locale.getString(LocaleKeys.NO_NETWORK_WHISPER_DESCRIPTION),
            {
                DEVICE_NAME_SHORT: service.getBrandingUiInfoForKey(
                    BrandingUiInfoKeys.DEVICE_NAME_SHORT,
                ),
                BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                    ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                    : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
            },
        );
        dispatch(showWhisper({ message: whisperMessage }));
    } else {
        const onePassSharedPreferenceEditor = new SharedPreferences(
            SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
        ).edit();
        const { currentHighlightIndex, horizontalOptions } = getState().programScreen;
        if (!horizontalOptions.length) {
            Log.e(TAG, 'Failed to get horizontal options.');
            Diagnostics.error(TAG, 'Failed to get horizontal options.');
            return;
        }
        let contentType = '';
        horizontalOptions.forEach((option: any) => {
            if (option.identifier?.kernel?.contentType) {
                contentType = option.identifier.kernel.contentType;
            }
        });
        const optionDetails = horizontalOptions[currentHighlightIndex].type;
        if (optionDetails === 'PosterImage' || optionDetails === 'LandscapeImage') {
            const { offerId, identifier, layout } =
                horizontalOptions[currentHighlightIndex].displayDetails;
            if (
                identifier === ProgramScreenConstants.EMPTY_STRIP ||
                identifier === ProgramScreenConstants.LOADING_STRIP
            )
                return;
            if (
                [
                    ProgramScreenConstants.ALL_EPISODES,
                    ProgramScreenConstants.ON_DEMAND_EPISODES,
                ].includes(layout)
            ) {
                /* TODO : Need to add this when season picker capability is implemented
                 for list view screen */

                if (layout === ProgramScreenConstants.ALL_EPISODES) {
                    const currScreen = ScreenController.getInstance().getScreen();
                    const currScreenProperties = currScreen.getProperties() as TProgramScreenState;
                    const psSharedPref = new SharedPreferences(
                        SharedPrefConstants.PROGRAM_SCREEN_PREF_KEY,
                    );
                    const titleJson = psSharedPref.getJson(
                        SharedPrefConstants.PROGRAM_SCREEN_TITLE,
                    ) as unknown as {
                        title: string;
                        isGame: boolean;
                    };
                    const currentAction = psSharedPref.getJson(
                        SharedPrefConstants.PROGRAM_SCREEN_HORIZONTAL_ACTION,
                    ) as unknown as SeasonOrYearsDetail;
                    dispatch(setListEntryPageActions(0));
                    dispatch(
                        showListViewScreen({
                            id: currentAction.collectionId,
                            title: titleJson?.title || '',
                            isAdult: false,
                            listType: 'allEpisodes',
                            collapsedVodItemId: currScreenProperties?.collapsedVodItemId || '',
                            partnerId: currScreenProperties?.partnerId || '',
                        }),
                    );
                } else if (layout === ProgramScreenConstants.ON_DEMAND_EPISODES) {
                    const isOnDemandEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
                        CcbSwitchName.ENABLE_ONDEMAND_EPISODE_REMOVE_IN_KST_12206,
                    );
                    if (!isOnDemandEnabled) {
                        Log.i(
                            TAG,
                            'On-Demand Episodes option is disabled via CCB Switch. Ignoring the action.',
                        );
                        return;
                    }

                    let numberOfItems: number = 1;
                    if (currentHighlightIndex) {
                        for (let i = 1; i < currentHighlightIndex; i += 1) {
                            numberOfItems += horizontalOptions[i]?.displayDetails?.episode + 1;
                        }
                    }
                    const programSharedPreferenceEditor = DeviceModel.getSharedPreferences(
                        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                    ).edit();
                    programSharedPreferenceEditor.putNumber(
                        SharedPrefConstants.LISTVIEW_HIGHLIGHT_INDEX,
                        numberOfItems,
                    );
                    programSharedPreferenceEditor.apply();

                    const currScreen = ScreenController.getInstance().getScreen();
                    const currScreenProperties = currScreen.getProperties() as TProgramScreenState;
                    const titleSharedPref = new SharedPreferences(
                        SharedPrefConstants.PROGRAM_SCREEN_PREF_KEY,
                    );
                    const titleJson = titleSharedPref.getJson(
                        SharedPrefConstants.PROGRAM_SCREEN_TITLE,
                    ) as unknown as {
                        title: string;
                        isGame: boolean;
                    };
                    const listScreenSharedPreferenceEditor = DeviceModel.getSharedPreferences(
                        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                    );
                    const unifiedCollectionId = listScreenSharedPreferenceEditor.getString(
                        SharedPrefConstants.LIST_SCREEN_ID,
                    );

                    dispatch(
                        showListViewScreen({
                            id: unifiedCollectionId,
                            title: titleJson?.title || '',
                            listType: 'onDemand',
                            isAdult: false,
                            collapsedVodItemId: currScreenProperties?.collapsedVodItemId || '',
                            partnerId: currScreenProperties?.partnerId || '',
                        }),
                    );
                } else {
                    Log.i(TAG, 'Seasons option selected, but not implemented yet.');
                    return;
                }
            }
            if (
                identifier === ProgramScreenConstants.VIEW_ALL &&
                [
                    ProgramScreenConstants.UPCOMING_AIRING,
                    ProgramScreenConstants.ALL_UPCOMING_EPISODES,
                ].includes(layout)
            ) {
                BiaxialActionWidgetHelper.reset();
                dispatch(setListEntryActions(0));
                dispatch(setListEntryPageActions(0));
                dispatch(
                    showListViewScreen({
                        id: BiaxialActionWidgetHelper.getId(),
                        title: ProgramScreenHelper.generateListViewTitle(layout),
                        isAdult: false,
                        listType: 'upcoming',
                    }),
                );
                return;
            }

            dispatch(transitionToProgramScreen(offerId, ContentType.OFFER_ID));
            return;
        }

        const action = horizontalOptions[currentHighlightIndex]?.identifier;
        if (!action) {
            Log.e(
                TAG,
                'Failed to get action from horizontal options.',
                horizontalOptions[currentHighlightIndex],
            );
            Diagnostics.error(
                TAG,
                'Failed to get action from horizontal options.',
                horizontalOptions[currentHighlightIndex],
            );
            return;
        }
        switch (action.kernel.actionKernelType) {
            case ActionKernelType.ModifyKeepUntilActionKernel:
                dispatch(prepareKeepUntilRecordingOverlay({ for: 'recordingId', action }));
                break;
            case ActionKernelType.CreateMultiOnePassActionKernel:
            case ActionKernelType.CreateOnePassActionKernelV2:
                onePassSharedPreferenceEditor.putJson(
                    SharedPrefConstants.CREATE_ONE_PASS_ACTION,
                    action,
                );
                onePassSharedPreferenceEditor.apply();
                dispatch(prepareCreateOnePassOverlay());
                break;
            case ActionKernelType.ModifyMultiOnePassActionKernel:
            case ActionKernelType.ModifyOnePassActionKernelV2:
                onePassSharedPreferenceEditor.putJson(
                    SharedPrefConstants.MODIFY_ONE_PASS_ACTION,
                    action,
                );
                onePassSharedPreferenceEditor.apply();
                dispatch(prepareModifyOnePassOverlay());
                break;
            case ActionKernelType.CancelMultiOnePassActionKernel:
            case ActionKernelType.CancelOnePassActionKernelV2:
                onePassSharedPreferenceEditor.putString(
                    SharedPrefConstants.CANCEL_ONE_PASS_ACTION,
                    action,
                );
                onePassSharedPreferenceEditor.apply();
                dispatch(prepareCancelOnePassOverlay());
                break;
            case ActionKernelType.ModifyRecordingActionKernel: {
                const { contentId, offerId, actionKernelType } = action.kernel;
                const { actionId: cancelActionId } = action.kernel?.cancelAction?.kernel;
                setRecordingSharedPref({
                    contentId,
                    offerId,
                    cancelActionId,
                });
                dispatch(
                    showRecordingOverlay(contentType, { contentId, offerId }, actionKernelType),
                );
                break;
            }
            case ActionKernelType.RecordAgainActionKernel:
            case ActionKernelType.RecordThisEpisodeActionKernel:
            case ActionKernelType.RecordThisEventActionKernel:
            case ActionKernelType.RecordThisGameActionKernel:
            case ActionKernelType.RecordThisMatchActionKernel:
            case ActionKernelType.RecordThisMovieActionKernel:
            case ActionKernelType.RecordThisRaceActionKernel:
            case ActionKernelType.RecordThisShowActionKernel: {
                // The action API response segment is of the form :
                // "kernel":{
                // "actionId":"RECORD_THIS_EPISODE",
                // "actionKernelType":"recordThisEpisodeActionKernel",
                // "channelId":"tivo:ch.8105",
                // "channelNumber":"",
                // "contentId":"tivo:ct.513898191",
                // "offerId":"tivo:of.std.10420137.2025-02-02-17-00-00.3600"
                // },
                const { contentId, offerId, actionKernelType } = action.kernel;
                setRecordingSharedPref({
                    contentId,
                    offerId,
                });
                const recordingActionId = action.kernel.actionId.split('_');
                const recordingType = recordingActionId[recordingActionId.length - 1].toLowerCase();
                dispatch(
                    showRecordingOverlay(recordingType, { contentId, offerId }, actionKernelType),
                );
                break;
            }
            case ActionKernelType.StopAndDeleteRecordingActionKernel: {
                setRecordingSharedPref({
                    contentId: action.kernel.contentId,
                    offerId: action.kernel.offerId,
                });
                dispatch(showStopAndDeleteRecordingOverlay(contentType));
                break;
            }
            case ActionKernelType.CancelRecordingActionKernel: {
                setRecordingSharedPref({
                    contentId: action.kernel.contentId,
                    offerId: action.kernel.offerId,
                });
                dispatch(showCancelRecordingOverlay(contentType));
                break;
            }
            case ActionKernelType.DeleteBookmarkActionKernel:
                dispatch(showDeleteOverlay(action));
                break;
            case ActionKernelType.DeleteRecordingActionKernel:
                dispatch(showDeleteOverlay(action));
                break;
            case ActionKernelType.RecoverRecordingActionKernel:
                new NdvrInteractor()
                    .getRequestModelToRecordingRecovery(action.kernel.recordingId)
                    .send()
                    .then(() => {
                        UIOrchestrator.stateChangeListener.notify(
                            UIOrchestratorState.RECORDING_RECOVERED,
                        );
                    });
                break;
            case ActionKernelType.BookmarkThisEpisodeActionKernel:
            case ActionKernelType.BookmarkThisEventActionKernel:
            case ActionKernelType.BookmarkThisGameActionKernel:
            case ActionKernelType.BookmarkThisMatchActionKernel:
            case ActionKernelType.BookmarkThisMovieActionKernel:
            case ActionKernelType.BookmarkThisRaceActionKernel:
            case ActionKernelType.BookmarkThisShowActionKernel: {
                // The action API response segment is of the form :
                // "kernel":{
                // "actionId":"BOOKMARK_THIS_EPISODE",
                // "actionKernelType":"bookmarkThisEpisodeActionKernel",
                // "collectionId":"tivo:cl.24342358",
                // "contentId":"tivo:ct.144864487"
                // },
                dispatch(createBookmark('content', action));
                break;
            }
            case ActionKernelType.AddSeasonStreamingVideoActionKernel:
            case ActionKernelType.AddYearStreamingVideoActionKernel: {
                // The action API response segment is of the form :
                // "kernel":{
                // "actionId":"ADD_SEASON_STREAMING_VIDEO",
                // "actionKernelType":"addSeasonStreamingVideoActionKernel",
                // "collectionId":"tivo:cl.354287155",
                // "seasonNumber":2024
                // },
                dispatch(createBookmark('collection', action));
                break;
            }
            case ActionKernelType.MoreInfoContentActionKernel: {
                dispatch(
                    transitionToProgramScreen(action.kernel.contentId, ContentType.CONTENT_ID),
                );
                break;
            }
            case ActionKernelType.InfoContentActionKernel:
                if (
                    CcbSwitch.getInstance().isEnabledAndOverriden(
                        CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
                    )
                ) {
                    let infoCardKernel: InfoCardKernel;
                    const { displayDetails } = horizontalOptions[currentHighlightIndex];
                    if (displayDetails.action) {
                        infoCardKernel = displayDetails.action.infoCardKernel;
                    } else {
                        Log.e(
                            'ProgramScreenReducers',
                            'onKeyOk-Info: No suitable infocard kernel found to trigger info card',
                        );
                        return;
                    }
                    const infoCard: IInfoCard = InfoCardManager.getInstance();
                    infoCard.setInfoCardKernel(infoCardKernel);
                    infoCard.show();
                }
                break;
            default:
                dispatch(handlePlayableAction(action));
        }
    }
};

export const onKeyRightProgramScreenHandler =
    () => async (dispatch: Function, getState: TGetState) => {
        const { programScreen } = getState();
        const { totalItems, currentHighlightIndex, horizontalOptions } = programScreen;
        if (!horizontalOptions[currentHighlightIndex]) return;
        const optionDetails = horizontalOptions[currentHighlightIndex]?.type;
        const layoutType = horizontalOptions[currentHighlightIndex]?.displayDetails?.layout;
        const isStaticDataStrip = [
            ProgramScreenConstants.ALL_EPISODES,
            ProgramScreenConstants.ON_DEMAND_EPISODES,
            ProgramScreenConstants.CAST,
        ].includes(layoutType);

        if (
            ((optionDetails && optionDetails === 'PosterImage') ||
                optionDetails === 'LandscapeImage') &&
            layoutType &&
            !isStaticDataStrip
        ) {
            const tCount = BiaxialActionWidgetHelper.getTotalCount();
            const limit = BiaxialActionWidgetHelper.getLimit();
            if (tCount === null || tCount <= 1) return;

            dispatch(setRightGradientVisibleAction(true));
            setTimeout(() => {
                dispatch(setRightGradientVisibleAction(false));
            }, 300);

            const nextIdx = BiaxialActionWidgetHelper.getNextOffset('right');

            // If tCount <= limit, Skip loading state as everything is already cached
            if (tCount > limit) {
                const curIdx = BiaxialActionWidgetHelper.getCurrentImageIndex();
                const loadingState = BiaxialActionWidgetHelper.getLoadingState(
                    curIdx,
                    nextIdx,
                    'right',
                    horizontalOptions,
                );
                dispatch(setHorizontalOptionsProgramScreenAction(loadingState));
            }

            const res = await BiaxialActionWidgetHelper.fetchData(nextIdx);

            // If tCount <= limit, Skip networkId check as everything is cached and static
            if (
                tCount <= limit ||
                res.networkCallId === UniqueIdentifier.getUpcomingNetworkCallId()
            ) {
                dispatch(setHorizontalOptionsProgramScreenAction(res.imageInfo));
                dispatch(updateViewAllDirectionProgramScreen());
            }
            return;
        }

        // TODO: Need to Fix this in KST-11230
        const newHighlightIndex = (currentHighlightIndex + 1) % totalItems;
        if (newHighlightIndex !== currentHighlightIndex)
            dispatch(setCurrentHighlightIndexProgramScreenAction(newHighlightIndex));
        dispatch(updateViewAllDirectionProgramScreen());
    };

export const onKeyUpProgramScreenHandler = () => (dispatch: Function, getState: Function) => {
    const { programScreen } = getState();
    const { currentVerticalHighlightIndex } = programScreen;
    const newHighlightIndex = Math.max(currentVerticalHighlightIndex - 1, 0);
    dispatch(setCurrentVerticalHighlightIndexProgramScreenAction(newHighlightIndex));
    if (currentVerticalHighlightIndex) {
        dispatch(setHorizontalOptionsProgramScreenAction([]));
        BiaxialActionWidgetHelper.reset();
    }
};

export const onInfoCardDismissProgramScreen =
    () => async (dispatch: AppDispatch, getState: TGetState) => {
        const { currentHighlightIndex, horizontalOptions } = getState().programScreen;
        const { displayDetails } = horizontalOptions[currentHighlightIndex];

        if (
            [
                ProgramScreenConstants.UPCOMING_AIRING,
                ProgramScreenConstants.ALL_UPCOMING_EPISODES,
            ].includes(displayDetails.identifier)
        ) {
            const infoCard: IInfoCard = InfoCardManager.getInstance();
            const { offset } = infoCard.getContextData(
                ProgramScreenInfoCardUserInputHandler.name,
            ) as { offset: number };
            if (offset === BiaxialActionWidgetHelper.getCurrentImageIndex()) return;
            const res = await BiaxialActionWidgetHelper.fetchData(offset);
            dispatch(setHorizontalOptionsProgramScreenAction(res.imageInfo));
            dispatch(updateViewAllDirectionProgramScreen());
        }
    };

export default programScreenReducer;
